export const ROLE_RESOLUTION_SOURCES = Object.freeze({
  INVITE: "invite",
  METADATA: "metadata",
  PROFILE_SELECTED: "profile_selected",
  PROFILE_OWNERSHIP: "profile_ownership",
  PHARMACY_ACTIVITY: "pharmacy_activity",
  USER_ROLES: "user_roles",
  PRACTICE_TEAM: "practice_team",
  FALLBACK_HINT: "fallback_hint",
});

export const REQUIRED_POST_AUTH_DIAGNOSTIC_STAGES = Object.freeze([
  "auth_callback_started",
  "auth_callback_completed",
  "role_resolution_started",
  "role_resolution_completed",
  "role_resolution_conflict",
  "onboarding_route_selected",
  "onboarding_first_render",
  "onboarding_blank_timeout",
]);

export const POST_AUTH_FALLBACK_ACTIONS = Object.freeze([
  "Retry",
  "Send new link",
  "Back to login",
  "Contact support",
]);

export function normalizeCanonicalRole(value) {
  const role = String(value || "").toLowerCase().trim().replace(/[\s-]+/g, "_");
  if (
    role === "clinician" ||
    role === "clinical" ||
    role === "provider" ||
    role === "doctor_reviewer" ||
    role === "clinical_reviewer"
  ) return "doctor";
  if (role === "patient_reviewer") return "patient";
  if (role === "pharmacist" || role === "pharmd" || role === "pharmacy_owner" || role === "pharmacy_staff") {
    return "pharmacy";
  }
  if (role === "moh" || role === "ministry_admin") return "moh_admin";
  if (role === "institution") return "institution_admin";
  if (
    role === "doctor" ||
    role === "patient" ||
    role === "admin" ||
    role === "support" ||
    role === "kyc_reviewer" ||
    role === "pharmacy" ||
    role === "practice_team" ||
    role === "super_admin" ||
    role === "moh_admin" ||
    role === "institution_admin" ||
    role === "facility_admin"
  ) {
    return role;
  }
  return "";
}

export function hasMeaningfulRoleProfile(row, role) {
  if (!row?.user_id) return false;
  const values =
    role === "doctor"
      ? [
          row.onboarding_completed,
          row.full_name,
          row.first_name,
          row.last_name,
          row.specialty,
          row.speciality,
          row.specialization,
          row.about,
          row.profile_photo_path,
          row.profile_photo_storage_path,
          row.photo_path,
          row.avatar_path,
          row.email,
          row.phone,
        ]
      : [
          row.onboarding_completed,
          row.full_name,
          row.first_name,
          row.last_name,
          row.date_of_birth,
          row.street_address,
          row.city,
          row.country,
          row.languages,
          row.medical_conditions,
          row.medical_history,
          row.current_medications,
          row.allergies,
          row.blood_type,
          row.national_id_number,
          row.national_id,
          row.profile_photo_path,
          row.profile_photo_storage_path,
          row.photo_path,
          row.avatar_path,
          row.email,
          row.phone,
        ];

  return values.some((value) => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "object") return Object.values(value).some(Boolean);
    if (typeof value === "string") {
      const raw = value.trim();
      return raw && raw !== "[]" && raw !== "{}" && raw !== "null" && raw !== "undefined";
    }
    return Boolean(value);
  });
}

export function resolveCanonicalRole({
  inviteRole = "",
  metadataRole = "",
  profileRole = "",
  roleSelected = false,
  doctorProfile = null,
  patientProfile = null,
  pharmacyActivity = false,
  practiceTeamMembership = false,
  userRoles = [],
  fallbackRole = "",
} = {}) {
  const evidence = [];
  const conflicts = [];
  const push = (source, role, trusted = true) => {
    const normalized = normalizeCanonicalRole(role);
    if (!normalized) return "";
    evidence.push({ source, role: normalized, trusted });
    return normalized;
  };

  const invite = push(ROLE_RESOLUTION_SOURCES.INVITE, inviteRole);
  const metadata = push(ROLE_RESOLUTION_SOURCES.METADATA, metadataRole);
  const selected = roleSelected === true
    ? push(ROLE_RESOLUTION_SOURCES.PROFILE_SELECTED, profileRole)
    : "";
  const pharmacySignal = pharmacyActivity === true
    ? push(ROLE_RESOLUTION_SOURCES.PHARMACY_ACTIVITY, "pharmacy")
    : "";
  const practiceTeamSignal = practiceTeamMembership === true
    ? push(ROLE_RESOLUTION_SOURCES.PRACTICE_TEAM, "practice_team")
    : "";

  const hasDoctor = !!doctorProfile?.user_id;
  const hasPatient = !!patientProfile?.user_id;
  const doctorMeaningful = hasMeaningfulRoleProfile(doctorProfile, "doctor");
  const patientMeaningful = hasMeaningfulRoleProfile(patientProfile, "patient");

  let ownership = "";
  if (hasDoctor && !hasPatient) ownership = "doctor";
  if (!hasDoctor && hasPatient && patientMeaningful) ownership = "patient";
  if (hasDoctor && hasPatient) {
    if (doctorMeaningful && !patientMeaningful) ownership = "doctor";
    if (!doctorMeaningful && patientMeaningful) ownership = "patient";
  }
  if (ownership) push(ROLE_RESOLUTION_SOURCES.PROFILE_OWNERSHIP, ownership);

  const roles = Array.from(
    new Set((Array.isArray(userRoles) ? userRoles : []).map(normalizeCanonicalRole).filter(Boolean)),
  );
  const institutionalRoleFromUserRoles = roles.find((role) =>
    role === "super_admin" ||
    role === "moh_admin" ||
    role === "institution_admin" ||
    role === "facility_admin"
  ) || "";
  const roleFromUserRoles = institutionalRoleFromUserRoles || (roles.length === 1 ? roles[0] : "");
  if (roleFromUserRoles) push(ROLE_RESOLUTION_SOURCES.USER_ROLES, roleFromUserRoles);

  const fallback = push(ROLE_RESOLUTION_SOURCES.FALLBACK_HINT, fallbackRole, false);
  const ordered = [
    [ROLE_RESOLUTION_SOURCES.INVITE, invite],
    [ROLE_RESOLUTION_SOURCES.PHARMACY_ACTIVITY, pharmacySignal],
    [ROLE_RESOLUTION_SOURCES.METADATA, metadata],
    [ROLE_RESOLUTION_SOURCES.PROFILE_SELECTED, selected],
    [ROLE_RESOLUTION_SOURCES.USER_ROLES, institutionalRoleFromUserRoles],
    [ROLE_RESOLUTION_SOURCES.USER_ROLES, roleFromUserRoles],
    [ROLE_RESOLUTION_SOURCES.PROFILE_OWNERSHIP, ownership],
    [ROLE_RESOLUTION_SOURCES.PRACTICE_TEAM, practiceTeamSignal],
    [ROLE_RESOLUTION_SOURCES.FALLBACK_HINT, fallback],
  ];

  const resolvedRole = ordered.find(([, role]) => !!role)?.[1] || "";

  for (const item of evidence) {
    if (item.role && resolvedRole && item.role !== resolvedRole) {
      conflicts.push({
        source: item.source,
        role: item.role,
        winningRole: resolvedRole,
      });
    }
  }

  return {
    role: resolvedRole,
    source: ordered.find(([, role]) => role === resolvedRole)?.[0] || "",
    evidence,
    conflicts,
    hasDoctorProfile: hasDoctor,
    hasPatientProfile: hasPatient,
    doctorProfileMeaningful: doctorMeaningful,
    patientProfileMeaningful: patientMeaningful,
  };
}

export function selectPostAuthRoute({ role = "", onboardingCompleted = false } = {}) {
  const safeRole = normalizeCanonicalRole(role);
  if (safeRole === "admin") return "/admin/kpi";
  if (
    safeRole === "super_admin" ||
    safeRole === "moh_admin" ||
    safeRole === "institution_admin" ||
    safeRole === "facility_admin"
  ) {
    return "/institutions/dashboard";
  }
  if (safeRole === "pharmacy") return onboardingCompleted ? "/pharmacy/dashboard" : "/onboarding/pharmacy";
  if (safeRole === "practice_team") return "/practice-team/welcome";
  if (safeRole === "doctor") {
    return onboardingCompleted ? "/home/doctor" : "/onboarding/doctor?role=doctor";
  }
  if (safeRole === "patient") {
    return onboardingCompleted ? "/home/patient" : "/onboarding/patient?role=patient";
  }
  return "/onboarding";
}

// An opaque campaign id is only written by the server-side controlled staging
// access function. It makes the first-use coordinator explicit without
// accepting a mutable `?role=` value from the browser.
export function selectControlledFirstUseRoute({
  role = "",
  onboardingCompleted = false,
  campaignId = "",
} = {}) {
  const safeRole = normalizeCanonicalRole(role);
  const hasServerCampaign = Boolean(String(campaignId || "").trim());
  if (!hasServerCampaign || onboardingCompleted) {
    return selectPostAuthRoute({ role: safeRole, onboardingCompleted });
  }
  if (safeRole === "doctor" || safeRole === "pharmacy") {
    return "/onboarding?first_use=1";
  }
  if (safeRole === "patient") return "/onboarding/patient?first_use=1";
  if (safeRole === "practice_team") return "/practice-team/welcome?first_use=1";
  return selectPostAuthRoute({ role: safeRole, onboardingCompleted });
}

export async function fetchCanonicalRoleResolution({
  supabase,
  user,
  fallbackRole = "",
  includeInvite = true,
  practiceTeamMembershipResolver = null,
} = {}) {
  const userId = String(user?.id || "").trim();
  const email = String(user?.email || user?.user_metadata?.email || "").trim().toLowerCase();
  const metadataRole =
    normalizeCanonicalRole(user?.user_metadata?.primary_role) ||
    normalizeCanonicalRole(user?.user_metadata?.role) ||
    normalizeCanonicalRole(user?.app_metadata?.role);

  if (!supabase || !userId) {
    return resolveCanonicalRole({ metadataRole, fallbackRole });
  }

  const safeMaybeSingle = async (builder) => {
    try {
      const { data, error } = await builder.maybeSingle();
      if (error) throw error;
      return data || null;
    } catch {
      return null;
    }
  };

  const safeList = async (builder) => {
    try {
      const { data, error } = await builder;
      if (error) throw error;
      return Array.isArray(data) ? data : [];
    } catch {
      return [];
    }
  };

  const safePracticeTeamMembership = async () => {
    try {
      if (typeof practiceTeamMembershipResolver === "function") {
        return await practiceTeamMembershipResolver();
      }
      const { data, error } = await supabase.functions.invoke(
        "practice-team-invite-lifecycle",
        {
          body: {
            action: "welcome",
            member_id: "",
          },
        },
      );
      if (error) throw error;
      return data?.invite || null;
    } catch {
      return null;
    }
  };

  const [invite, profile, doctorProfile, patientProfile, roleRows, practiceTeamRows] = await Promise.all([
    includeInvite && email
      ? safeMaybeSingle(
          supabase
            .from("staging_access_invites")
            .select("role_hint,allowed")
            .eq("email", email)
            .eq("allowed", true)
            .order("updated_at", { ascending: false })
            .limit(1),
        )
      : Promise.resolve(null),
    safeMaybeSingle(
      supabase
        .from("profiles")
        .select("user_id,role,role_selected,onboarding_completed")
        .eq("user_id", userId),
    ),
    safeMaybeSingle(
      supabase
        .from("doctor_profiles")
        .select("*")
        .eq("user_id", userId),
    ),
    safeMaybeSingle(
      supabase
        .from("patient_profiles")
        .select("*")
        .eq("user_id", userId),
    ),
    safeList(
      supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId),
    ),
    safePracticeTeamMembership(),
  ]);
  const [pharmacyOnboardingEvents, pharmacyActivityEvents] = await Promise.all([
    safeList(
      supabase
        .from("onboarding_events")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(25),
    ),
    safeList(
      supabase
        .from("user_activity_events")
        .select("*")
        .eq("user_id", userId)
        .order("created_at", { ascending: false })
        .limit(50),
    ),
  ]);
  const hasPharmacyActivity = [...pharmacyOnboardingEvents, ...pharmacyActivityEvents].some((row) => {
    const route = String(row?.route || "").trim().toLowerCase();
    const eventName = String(row?.event || row?.event_type || "").trim().toLowerCase();
    const stepName = String(row?.step || row?.step_name || "").trim().toLowerCase();
    const role = normalizeCanonicalRole(row?.role);
    const meta = row?.metadata || row?.meta || {};
    return (
      role === "pharmacy" ||
      route.startsWith("/pharmacy") ||
      route.startsWith("/onboarding/pharmacy") ||
      eventName.includes("pharmacy") ||
      stepName.includes("pharmacy") ||
      meta?.role === "pharmacy" ||
      meta?.lane === "pharmacy" ||
      meta?.normalized_role === "pharmacy" ||
      meta?.role_hint === "pharmacy"
    );
  });

  return {
    ...resolveCanonicalRole({
      inviteRole: invite?.role_hint || "",
      metadataRole,
      profileRole: profile?.role || "",
      roleSelected: profile?.role_selected === true,
      doctorProfile,
      patientProfile,
      pharmacyActivity: hasPharmacyActivity,
      practiceTeamMembership: !!practiceTeamRows?.id,
      userRoles: roleRows.map((row) => row?.role),
      fallbackRole,
    }),
    profile,
    doctorProfile,
    patientProfile,
  };
}
